import {
  CountryType,
  Category,
  GSELevel,
  Vendor,
  Location,
  Children,
  ExclusionInterval,
  TemplateSession,
  ParsedSession,
  ApiErrorResponse,
} from './types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Extend dayjs with timezone support for getOffset function
dayjs.extend(utc);
dayjs.extend(timezone);

// From https://bitbucket.org/atlassian/atlaskit-mk-2/raw/4ad0e56649c3e6c973e226b7efaeb28cb240ccb0/packages/core/select/src/data/countries.js
export const countries: readonly CountryType[] = [
  { code: 'AD', label: 'Andorra', phone: '376' },
  {
    code: 'AE',
    label: 'United Arab Emirates',
    phone: '971',
  },
  { code: 'AF', label: 'Afghanistan', phone: '93' },
  {
    code: 'AG',
    label: 'Antigua and Barbuda',
    phone: '1-268',
  },
  { code: 'AI', label: 'Anguilla', phone: '1-264' },
  { code: 'AL', label: 'Albania', phone: '355' },
  { code: 'AM', label: 'Armenia', phone: '374' },
  { code: 'AO', label: 'Angola', phone: '244' },
  { code: 'AQ', label: 'Antarctica', phone: '672' },
  { code: 'AR', label: 'Argentina', phone: '54' },
  { code: 'AS', label: 'American Samoa', phone: '1-684' },
  { code: 'AT', label: 'Austria', phone: '43' },
  {
    code: 'AU',
    label: 'Australia',
    phone: '61',
    suggested: true,
  },
  { code: 'AW', label: 'Aruba', phone: '297' },
  { code: 'AX', label: 'Alland Islands', phone: '358' },
  { code: 'AZ', label: 'Azerbaijan', phone: '994' },
  {
    code: 'BA',
    label: 'Bosnia and Herzegovina',
    phone: '387',
  },
  { code: 'BB', label: 'Barbados', phone: '1-246' },
  { code: 'BD', label: 'Bangladesh', phone: '880' },
  { code: 'BE', label: 'Belgium', phone: '32' },
  { code: 'BF', label: 'Burkina Faso', phone: '226' },
  { code: 'BG', label: 'Bulgaria', phone: '359' },
  { code: 'BH', label: 'Bahrain', phone: '973' },
  { code: 'BI', label: 'Burundi', phone: '257' },
  { code: 'BJ', label: 'Benin', phone: '229' },
  { code: 'BL', label: 'Saint Barthelemy', phone: '590' },
  { code: 'BM', label: 'Bermuda', phone: '1-441' },
  { code: 'BN', label: 'Brunei Darussalam', phone: '673' },
  { code: 'BO', label: 'Bolivia', phone: '591' },
  { code: 'BR', label: 'Brazil', phone: '55' },
  { code: 'BS', label: 'Bahamas', phone: '1-242' },
  { code: 'BT', label: 'Bhutan', phone: '975' },
  { code: 'BV', label: 'Bouvet Island', phone: '47' },
  { code: 'BW', label: 'Botswana', phone: '267' },
  { code: 'BY', label: 'Belarus', phone: '375' },
  { code: 'BZ', label: 'Belize', phone: '501' },
  {
    code: 'CA',
    label: 'Canada',
    phone: '1',
    suggested: true,
  },
  {
    code: 'CC',
    label: 'Cocos (Keeling) Islands',
    phone: '61',
  },
  {
    code: 'CD',
    label: 'Congo, Democratic Republic of the',
    phone: '243',
  },
  {
    code: 'CF',
    label: 'Central African Republic',
    phone: '236',
  },
  {
    code: 'CG',
    label: 'Congo, Republic of the',
    phone: '242',
  },
  { code: 'CH', label: 'Switzerland', phone: '41' },
  { code: 'CI', label: "Cote d'Ivoire", phone: '225' },
  { code: 'CK', label: 'Cook Islands', phone: '682' },
  { code: 'CL', label: 'Chile', phone: '56' },
  { code: 'CM', label: 'Cameroon', phone: '237' },
  { code: 'CN', label: 'China', phone: '86' },
  { code: 'CO', label: 'Colombia', phone: '57' },
  { code: 'CR', label: 'Costa Rica', phone: '506' },
  { code: 'CU', label: 'Cuba', phone: '53' },
  { code: 'CV', label: 'Cape Verde', phone: '238' },
  { code: 'CW', label: 'Curacao', phone: '599' },
  { code: 'CX', label: 'Christmas Island', phone: '61' },
  { code: 'CY', label: 'Cyprus', phone: '357' },
  { code: 'CZ', label: 'Czech Republic', phone: '420' },
  {
    code: 'DE',
    label: 'Germany',
    phone: '49',
    suggested: true,
  },
  { code: 'DJ', label: 'Djibouti', phone: '253' },
  { code: 'DK', label: 'Denmark', phone: '45' },
  { code: 'DM', label: 'Dominica', phone: '1-767' },
  {
    code: 'DO',
    label: 'Dominican Republic',
    phone: '1-809',
  },
  { code: 'DZ', label: 'Algeria', phone: '213' },
  { code: 'EC', label: 'Ecuador', phone: '593' },
  { code: 'EE', label: 'Estonia', phone: '372' },
  { code: 'EG', label: 'Egypt', phone: '20' },
  { code: 'EH', label: 'Western Sahara', phone: '212' },
  { code: 'ER', label: 'Eritrea', phone: '291' },
  { code: 'ES', label: 'Spain', phone: '34' },
  { code: 'ET', label: 'Ethiopia', phone: '251' },
  { code: 'FI', label: 'Finland', phone: '358' },
  { code: 'FJ', label: 'Fiji', phone: '679' },
  {
    code: 'FK',
    label: 'Falkland Islands (Malvinas)',
    phone: '500',
  },
  {
    code: 'FM',
    label: 'Micronesia, Federated States of',
    phone: '691',
  },
  { code: 'FO', label: 'Faroe Islands', phone: '298' },
  {
    code: 'FR',
    label: 'France',
    phone: '33',
    suggested: true,
  },
  { code: 'GA', label: 'Gabon', phone: '241' },
  { code: 'GB', label: 'United Kingdom', phone: '44' },
  { code: 'GD', label: 'Grenada', phone: '1-473' },
  { code: 'GE', label: 'Georgia', phone: '995' },
  { code: 'GF', label: 'French Guiana', phone: '594' },
  { code: 'GG', label: 'Guernsey', phone: '44' },
  { code: 'GH', label: 'Ghana', phone: '233' },
  { code: 'GI', label: 'Gibraltar', phone: '350' },
  { code: 'GL', label: 'Greenland', phone: '299' },
  { code: 'GM', label: 'Gambia', phone: '220' },
  { code: 'GN', label: 'Guinea', phone: '224' },
  { code: 'GP', label: 'Guadeloupe', phone: '590' },
  { code: 'GQ', label: 'Equatorial Guinea', phone: '240' },
  { code: 'GR', label: 'Greece', phone: '30' },
  {
    code: 'GS',
    label: 'South Georgia and the South Sandwich Islands',
    phone: '500',
  },
  { code: 'GT', label: 'Guatemala', phone: '502' },
  { code: 'GU', label: 'Guam', phone: '1-671' },
  { code: 'GW', label: 'Guinea-Bissau', phone: '245' },
  { code: 'GY', label: 'Guyana', phone: '592' },
  { code: 'HK', label: 'Hong Kong', phone: '852' },
  {
    code: 'HM',
    label: 'Heard Island and McDonald Islands',
    phone: '672',
  },
  { code: 'HN', label: 'Honduras', phone: '504' },
  { code: 'HR', label: 'Croatia', phone: '385' },
  { code: 'HT', label: 'Haiti', phone: '509' },
  { code: 'HU', label: 'Hungary', phone: '36' },
  { code: 'ID', label: 'Indonesia', phone: '62' },
  { code: 'IE', label: 'Ireland', phone: '353' },
  { code: 'IL', label: 'Israel', phone: '972' },
  { code: 'IM', label: 'Isle of Man', phone: '44' },
  { code: 'IN', label: 'India', phone: '91' },
  {
    code: 'IO',
    label: 'British Indian Ocean Territory',
    phone: '246',
  },
  { code: 'IQ', label: 'Iraq', phone: '964' },
  {
    code: 'IR',
    label: 'Iran, Islamic Republic of',
    phone: '98',
  },
  { code: 'IS', label: 'Iceland', phone: '354' },
  { code: 'IT', label: 'Italy', phone: '39' },
  { code: 'JE', label: 'Jersey', phone: '44' },
  { code: 'JM', label: 'Jamaica', phone: '1-876' },
  { code: 'JO', label: 'Jordan', phone: '962' },
  {
    code: 'JP',
    label: 'Japan',
    phone: '81',
    suggested: true,
  },
  { code: 'KE', label: 'Kenya', phone: '254' },
  { code: 'KG', label: 'Kyrgyzstan', phone: '996' },
  { code: 'KH', label: 'Cambodia', phone: '855' },
  { code: 'KI', label: 'Kiribati', phone: '686' },
  { code: 'KM', label: 'Comoros', phone: '269' },
  {
    code: 'KN',
    label: 'Saint Kitts and Nevis',
    phone: '1-869',
  },
  {
    code: 'KP',
    label: "Korea, Democratic People's Republic of",
    phone: '850',
  },
  { code: 'KR', label: 'Korea, Republic of', phone: '82' },
  { code: 'KW', label: 'Kuwait', phone: '965' },
  { code: 'KY', label: 'Cayman Islands', phone: '1-345' },
  { code: 'KZ', label: 'Kazakhstan', phone: '7' },
  {
    code: 'LA',
    label: "Lao People's Democratic Republic",
    phone: '856',
  },
  { code: 'LB', label: 'Lebanon', phone: '961' },
  { code: 'LC', label: 'Saint Lucia', phone: '1-758' },
  { code: 'LI', label: 'Liechtenstein', phone: '423' },
  { code: 'LK', label: 'Sri Lanka', phone: '94' },
  { code: 'LR', label: 'Liberia', phone: '231' },
  { code: 'LS', label: 'Lesotho', phone: '266' },
  { code: 'LT', label: 'Lithuania', phone: '370' },
  { code: 'LU', label: 'Luxembourg', phone: '352' },
  { code: 'LV', label: 'Latvia', phone: '371' },
  { code: 'LY', label: 'Libya', phone: '218' },
  { code: 'MA', label: 'Morocco', phone: '212' },
  { code: 'MC', label: 'Monaco', phone: '377' },
  {
    code: 'MD',
    label: 'Moldova, Republic of',
    phone: '373',
  },
  { code: 'ME', label: 'Montenegro', phone: '382' },
  {
    code: 'MF',
    label: 'Saint Martin (French part)',
    phone: '590',
  },
  { code: 'MG', label: 'Madagascar', phone: '261' },
  { code: 'MH', label: 'Marshall Islands', phone: '692' },
  {
    code: 'MK',
    label: 'Macedonia, the Former Yugoslav Republic of',
    phone: '389',
  },
  { code: 'ML', label: 'Mali', phone: '223' },
  { code: 'MM', label: 'Myanmar', phone: '95' },
  { code: 'MN', label: 'Mongolia', phone: '976' },
  { code: 'MO', label: 'Macao', phone: '853' },
  {
    code: 'MP',
    label: 'Northern Mariana Islands',
    phone: '1-670',
  },
  { code: 'MQ', label: 'Martinique', phone: '596' },
  { code: 'MR', label: 'Mauritania', phone: '222' },
  { code: 'MS', label: 'Montserrat', phone: '1-664' },
  { code: 'MT', label: 'Malta', phone: '356' },
  { code: 'MU', label: 'Mauritius', phone: '230' },
  { code: 'MV', label: 'Maldives', phone: '960' },
  { code: 'MW', label: 'Malawi', phone: '265' },
  { code: 'MX', label: 'Mexico', phone: '52' },
  { code: 'MY', label: 'Malaysia', phone: '60' },
  { code: 'MZ', label: 'Mozambique', phone: '258' },
  { code: 'NA', label: 'Namibia', phone: '264' },
  { code: 'NC', label: 'New Caledonia', phone: '687' },
  { code: 'NE', label: 'Niger', phone: '227' },
  { code: 'NF', label: 'Norfolk Island', phone: '672' },
  { code: 'NG', label: 'Nigeria', phone: '234' },
  { code: 'NI', label: 'Nicaragua', phone: '505' },
  { code: 'NL', label: 'Netherlands', phone: '31' },
  { code: 'NO', label: 'Norway', phone: '47' },
  { code: 'NP', label: 'Nepal', phone: '977' },
  { code: 'NR', label: 'Nauru', phone: '674' },
  { code: 'NU', label: 'Niue', phone: '683' },
  { code: 'NZ', label: 'New Zealand', phone: '64' },
  { code: 'OM', label: 'Oman', phone: '968' },
  { code: 'PA', label: 'Panama', phone: '507' },
  { code: 'PE', label: 'Peru', phone: '51' },
  { code: 'PF', label: 'French Polynesia', phone: '689' },
  { code: 'PG', label: 'Papua New Guinea', phone: '675' },
  { code: 'PH', label: 'Philippines', phone: '63' },
  { code: 'PK', label: 'Pakistan', phone: '92' },
  { code: 'PL', label: 'Poland', phone: '48' },
  {
    code: 'PM',
    label: 'Saint Pierre and Miquelon',
    phone: '508',
  },
  { code: 'PN', label: 'Pitcairn', phone: '870' },
  { code: 'PR', label: 'Puerto Rico', phone: '1' },
  {
    code: 'PS',
    label: 'Palestine, State of',
    phone: '970',
  },
  { code: 'PT', label: 'Portugal', phone: '351' },
  { code: 'PW', label: 'Palau', phone: '680' },
  { code: 'PY', label: 'Paraguay', phone: '595' },
  { code: 'QA', label: 'Qatar', phone: '974' },
  { code: 'RE', label: 'Reunion', phone: '262' },
  { code: 'RO', label: 'Romania', phone: '40' },
  { code: 'RS', label: 'Serbia', phone: '381' },
  { code: 'RU', label: 'Russian Federation', phone: '7' },
  { code: 'RW', label: 'Rwanda', phone: '250' },
  { code: 'SA', label: 'Saudi Arabia', phone: '966' },
  { code: 'SB', label: 'Solomon Islands', phone: '677' },
  { code: 'SC', label: 'Seychelles', phone: '248' },
  { code: 'SD', label: 'Sudan', phone: '249' },
  { code: 'SE', label: 'Sweden', phone: '46' },
  { code: 'SG', label: 'Singapore', phone: '65' },
  { code: 'SH', label: 'Saint Helena', phone: '290' },
  { code: 'SI', label: 'Slovenia', phone: '386' },
  {
    code: 'SJ',
    label: 'Svalbard and Jan Mayen',
    phone: '47',
  },
  { code: 'SK', label: 'Slovakia', phone: '421' },
  { code: 'SL', label: 'Sierra Leone', phone: '232' },
  { code: 'SM', label: 'San Marino', phone: '378' },
  { code: 'SN', label: 'Senegal', phone: '221' },
  { code: 'SO', label: 'Somalia', phone: '252' },
  { code: 'SR', label: 'Suriname', phone: '597' },
  { code: 'SS', label: 'South Sudan', phone: '211' },
  {
    code: 'ST',
    label: 'Sao Tome and Principe',
    phone: '239',
  },
  { code: 'SV', label: 'El Salvador', phone: '503' },
  {
    code: 'SX',
    label: 'Sint Maarten (Dutch part)',
    phone: '1-721',
  },
  {
    code: 'SY',
    label: 'Syrian Arab Republic',
    phone: '963',
  },
  { code: 'SZ', label: 'Swaziland', phone: '268' },
  {
    code: 'TC',
    label: 'Turks and Caicos Islands',
    phone: '1-649',
  },
  { code: 'TD', label: 'Chad', phone: '235' },
  {
    code: 'TF',
    label: 'French Southern Territories',
    phone: '262',
  },
  { code: 'TG', label: 'Togo', phone: '228' },
  { code: 'TH', label: 'Thailand', phone: '66' },
  { code: 'TJ', label: 'Tajikistan', phone: '992' },
  { code: 'TK', label: 'Tokelau', phone: '690' },
  { code: 'TL', label: 'Timor-Leste', phone: '670' },
  { code: 'TM', label: 'Turkmenistan', phone: '993' },
  { code: 'TN', label: 'Tunisia', phone: '216' },
  { code: 'TO', label: 'Tonga', phone: '676' },
  { code: 'TR', label: 'Turkey', phone: '90' },
  {
    code: 'TT',
    label: 'Trinidad and Tobago',
    phone: '1-868',
  },
  { code: 'TV', label: 'Tuvalu', phone: '688' },
  {
    code: 'TW',
    label: 'Taiwan',
    phone: '886',
  },
  {
    code: 'TZ',
    label: 'United Republic of Tanzania',
    phone: '255',
  },
  { code: 'UA', label: 'Ukraine', phone: '380' },
  { code: 'UG', label: 'Uganda', phone: '256' },
  {
    code: 'US',
    label: 'United States',
    phone: '1',
    suggested: true,
  },
  { code: 'UY', label: 'Uruguay', phone: '598' },
  { code: 'UZ', label: 'Uzbekistan', phone: '998' },
  {
    code: 'VA',
    label: 'Holy See (Vatican City State)',
    phone: '379',
  },
  {
    code: 'VC',
    label: 'Saint Vincent and the Grenadines',
    phone: '1-784',
  },
  { code: 'VE', label: 'Venezuela', phone: '58' },
  {
    code: 'VG',
    label: 'British Virgin Islands',
    phone: '1-284',
  },
  {
    code: 'VI',
    label: 'US Virgin Islands',
    phone: '1-340',
  },
  { code: 'VN', label: 'Vietnam', phone: '84' },
  { code: 'VU', label: 'Vanuatu', phone: '678' },
  { code: 'WF', label: 'Wallis and Futuna', phone: '681' },
  { code: 'WS', label: 'Samoa', phone: '685' },
  { code: 'XK', label: 'Kosovo', phone: '383' },
  { code: 'YE', label: 'Yemen', phone: '967' },
  { code: 'YT', label: 'Mayotte', phone: '262' },
  { code: 'ZA', label: 'South Africa', phone: '27' },
  { code: 'ZM', label: 'Zambia', phone: '260' },
  { code: 'ZW', label: 'Zimbabwe', phone: '263' },
];

export const isCategory = (value: Category) => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    typeof value.id === 'number' &&
    'category' in value &&
    typeof value.category === 'string'
  );
};

export const isGSELevel = (value: GSELevel) => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    typeof value.id === 'number' &&
    'level' in value &&
    typeof value.level === 'string' &&
    'minGSE' in value &&
    typeof value.code === 'number' &&
    'maxGSE' in value &&
    typeof value.code === 'number'
  );
};

export const isVendor = (value: Vendor) => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    typeof value.id === 'number' &&
    'vendor' in value &&
    typeof value.vendor === 'string'
  );
};

export const isLocation = (value: Location) => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    typeof value.id === 'number' &&
    'location' in value &&
    typeof value.location === 'string' &&
    'code' in value &&
    typeof value.code === 'string'
  );
};

export const allGSELevels: GSELevel[] = [
  {
    id: 1,
    level: 'A1 (22-29)',
    minGSE: 22,
    maxGSE: 29,
    code: 'A1',
  },
  {
    id: 2,
    level: 'A2 (30-35)',
    minGSE: 30,
    maxGSE: 35,
    code: 'A2',
  },
  {
    id: 3,
    level: 'A2+ (36-42)',
    minGSE: 36,
    maxGSE: 42,
    code: 'A2+',
  },
  {
    id: 4,
    level: 'B1 (43-50)',
    minGSE: 43,
    maxGSE: 50,
    code: 'B1',
  },
  {
    id: 5,
    level: 'B1+ (51-58)',
    minGSE: 51,
    maxGSE: 58,
    code: 'B1+',
  },
  {
    id: 6,
    level: 'B2 (59-66)',
    minGSE: 59,
    maxGSE: 66,
    code: 'B2',
  },
  {
    id: 7,
    level: 'B2+ (67-75)',
    minGSE: 67,
    maxGSE: 75,
    code: 'B2+',
  },
  {
    id: 8,
    level: 'C1 (76-84)',
    minGSE: 76,
    maxGSE: 84,
    code: 'C1',
  },
  {
    id: 9,
    level: 'C2 (85-90)',
    minGSE: 85,
    maxGSE: 90,
    code: 'C2',
  },
];

export const allCategories: Category[] = [
  {
    id: 1,
    category: 'General English',
  },
  {
    id: 2,
    category: 'Grammar',
  },
  {
    id: 3,
    category: 'Workplace English',
  },
  {
    id: 4,
    category: 'Specific purpose',
  },
];

export const allLocations: Location[] = [
  {
    id: 1,
    location: 'Australia',
    code: 'HIGH $$',
  },
  {
    id: 2,
    location: 'North America',
    code: 'HIGH $',
  },
  {
    id: 3,
    location: 'Western Europe',
    code: 'MED $$$',
  },
  {
    id: 4,
    location: 'Eastern Europe',
    code: 'MED $$',
  },
  {
    id: 5,
    location: 'Middle East',
    code: 'MED $',
  },
  {
    id: 6,
    location: 'Latin America',
    code: 'LOW $$$',
  },
  {
    id: 7,
    location: 'South Asia',
    code: 'LOW $$',
  },
  {
    id: 8,
    location: 'Southeast Asia',
    code: 'LOW $',
  },
  {
    id: 9,
    location: 'Other',
    code: '',
  },
];

export const getFileType = (fileName: string) => {
  const fileType = fileName?.split('.').pop()?.toLowerCase();
  switch (fileType) {
    case 'mp4':
    case 'avi':
    case 'mov':
      return 'video';
    case 'mp3':
    case 'wav':
      return 'audio';
    case 'doc':
    case 'docx':
    case 'txt':
      return 'doc';
    case 'pdf':
      return 'pdf';
    case 'ppt':
    case 'pptx':
      return 'ppt';
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
      return 'image';
    default:
      return 'doc';
  }
};

interface Level {
  range: [number, number];
  level: string;
}

const levels: Level[] = [
  { range: [10, 29], level: 'A1' },
  { range: [30, 35], level: 'A2' },
  { range: [36, 42], level: 'A2+' },
  { range: [43, 50], level: 'B1' },
  { range: [51, 58], level: 'B1+' },
  { range: [59, 66], level: 'B2' },
  { range: [67, 75], level: 'B2+' },
  { range: [76, 84], level: 'C1' },
  { range: [85, 90], level: 'C2' },
];

export const gseRangeToCEFR = (min: number, max: number) => {
  const compatibleLevels: string[] = [];

  levels.forEach(({ range, level }) => {
    const [rangeMin, rangeMax] = range;
    if ((min <= rangeMax && max >= rangeMin) || (max >= rangeMin && min <= rangeMax)) {
      compatibleLevels.push(level);
    }
  });

  return compatibleLevels;
};

export const getFormattedGender = (genderId: string) => {
  switch (genderId) {
    case 'MALE':
      return 'Male only';

    case 'FEMALE':
      return 'Female only';

    default:
      return 'No preference';
  }
};

interface HasId {
  id: number;
}

export const haveSameItems = <T extends HasId>(array1: T[], array2: T[]): boolean => {
  // Check if both arrays have the same length
  if (array1.length !== array2.length) {
    return false;
  }

  // Sort both arrays by id
  array1.sort((a, b) => a.id - b.id);
  array2.sort((a, b) => a.id - b.id);

  // Compare items in both arrays
  for (let i = 0; i < array1.length; i++) {
    if (array1[i].id !== array2[i].id) {
      return false;
    }
  }

  return true;
};

export const findNodeById = (roots: Children[], targetId: number): Children | null => {
  for (const root of roots) {
    if (root.children !== undefined) {
      const result = searchNodes(root.children, targetId);
      if (result !== null) {
        return result;
      }
    }
  }
  return null;
};

function searchNodes(nodes: Children[], targetId: number): Children | null {
  nodes.find((node) => {
    if (node.id === targetId) {
      return node;
    }

    if (node.children) {
      for (const child of node.children) {
        const result = searchNode(child, targetId);
        if (result !== null) {
          return result;
        }
      }
    }
  });

  return null;
}

function searchNode(node: Children, targetId: number): Children | null {
  if (node.id === targetId) {
    return node;
  }

  if (node.children) {
    for (const child of node.children) {
      const result = searchNode(child, targetId);
      if (result !== null) {
        return result;
      }
    }
  }

  return null;
}

export const findRootNodeByName = (rootName: string, allOrgs: Children[]) => {
  if (allOrgs) {
    return allOrgs.find((org) => org.name === rootName);
  }
  return null;
};

function parseDate(dateString: string) {
  const [day, month, year] = dateString.split('/').map(Number);
  return new Date(year, month - 1, day); // month is zero-based in JavaScript Date
}

// Function to calculate the difference between two dates in days
export function calculateDateDifference(dateString1: string, dateString2: string) {
  const date1: Date = parseDate(dateString1);
  const date2: Date = parseDate(dateString2);

  // Calculate the difference in milliseconds
  const differenceInMilliseconds = date2.valueOf() - date1.valueOf();

  // Convert milliseconds to days
  const differenceInDays = differenceInMilliseconds / (1000 * 60 * 60 * 24);

  return differenceInDays;
}

export const generate4DigitId = () => {
  return Math.floor(1000 + Math.random() * 9000); // Generates a random 4-digit number
};

// datepicker exclude days by schedulingWindow:
export const getDayKeyAsDayNumber = (key: string) => {
  switch (key) {
    case 'MO':
      return 1;
    case 'TU':
      return 2;
    case 'WE':
      return 3;
    case 'TH':
      return 4;
    case 'FR':
      return 5;
    case 'SA':
      return 6;
    case 'SU':
      return 0;
  }
};

function isDateDisabled(date: string, disabledDates: ExclusionInterval[]): boolean {
  return disabledDates.some(({ startDate, endDate }) => {
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    return (
      dayjs(date).isSame(start) ||
      dayjs(date).isSame(end) ||
      (dayjs(date).isAfter(startDate, 'day') && dayjs(date).isBefore(endDate, 'day'))
    );
  });
}

export function getFirstAvailableDate(
  initialStartDate: string,
  disabledDates: ExclusionInterval[],
  allowedDaysOfWeek?: number[],
): string | null {
  let date = dayjs(initialStartDate);

  for (let i = 0; i < 365; i++) {
    let isDisabled = false;
    if (disabledDates && disabledDates.length > 0) {
      isDisabled = isDateDisabled(dayjs(date).format('YYYY-MM-DD'), disabledDates);
    }
    const isAllowedDay = !allowedDaysOfWeek || allowedDaysOfWeek.includes(date.day());
    if (!isDisabled && isAllowedDay) {
      return date.format('YYYY-MM-DD');
    }
    date = date.add(1, 'day');
  }
  return initialStartDate;
}

export const findFirstChildWithClassSize = (node: Children | null): Children | null => {
  if (!node) {
    return null;
  }
  if (node && node.children) {
    if (node.children[0].classSize !== undefined && node.children[0].classSize !== null) {
      return node.children[0];
    } else {
      const nestedChild = findFirstChildWithClassSize(node.children[0]);
      if (nestedChild) return nestedChild;
    }
  }
  return null;
};

// Function to convert parsed CSV data to TemplateSession format
// export const convertParsedDataToTemplateSessions = (
//   parsedData: ParsedSession[],
// ): TemplateSession[] => {
//   return parsedData
//     .filter((row) => row.Time && row.Vendor) // Filter out empty rows
//     .map((row) => {
//       const startTime = row.Time.trim();
//       const weekday = row.Day.toLowerCase() === 'weekday';

//       const category = row.Categories.split(';').map((cat: string) => {
//         const trimmedCat = cat.trim();
//         const foundCategory = allCategories.find((c) => c.category === trimmedCat);
//         return foundCategory || { id: 0, category: trimmedCat }; // Handle unknown categories
//       });

//       const GSELevel = row['CEFR/GSE level'].split(';').map((level: string) => {
//         const trimmedLevel = level.trim();
//         const foundLevel = allGSELevels.find((g) => g.code === trimmedLevel);
//         return foundLevel || { id: 0, level: trimmedLevel, minGSE: 0, maxGSE: 0, code: '' }; // Handle unknown levels
//       });

//       const tutorRate = (() => {
//         const rateCode = row['Regional tutor rate'].split('-')[0].trim();
//         return allLocations.find((loc) => loc.code === rateCode);
//       })();

//       return {
//         startTime,
//         weekday,
//         category,
//         GSELevel,
//         tutorRate,
//         vendor: row.Vendor.trim(),
//       };
//     });
// };

export const convertParsedDataToTemplateSessions = (
  parsedData: ParsedSession[],
): { sessions: TemplateSession[]; errors: string[] } => {
  const errors: string[] = [];
  const sessions = parsedData
    // Step 1: Filter out completely blank rows
    .filter((row, index) => {
      const isEmptyRow = Object.values(row).every((value) => !value || value.trim() === '');
      if (isEmptyRow) {
        console.warn(`Row ${index + 1}: Completely blank row skipped.`);
      }
      return !isEmptyRow;
    })
    // Step 2: Validate each row for missing values
    .map((row, index) => {
      // Check if all required fields have values
      const missingFields: string[] = [];
      if (!row.Time) {
        missingFields.push('Time');
      }
      if (!row.Day) {
        missingFields.push('Day');
      }
      if (!row['CEFR/GSE level']) {
        missingFields.push('CEFR/GSE level');
      }
      if (!row['Regional tutor rate']) {
        missingFields.push('Regional tutor rate');
      }
      if (!row.Vendor) {
        missingFields.push('Vendor');
      }

      if (missingFields.length > 0) {
        errors.push(`Row ${index + 1}: Missing required fields: ${missingFields.join(', ')}`);
        return null;
      }

      try {
        // Process valid rows
        const startTime = row.Time.trim();
        const weekday = row.Day?.toLowerCase() === 'weekday';

        const category = row.Categories.split(';').map((cat: string) => {
          const trimmedCat = cat.trim();
          const foundCategory = allCategories.find((c) => c.category === trimmedCat);
          return foundCategory || { id: 0, category: trimmedCat }; // Handle unknown categories
        });

        const GSELevel = row['CEFR/GSE level']?.split(';').map((level: string) => {
          const trimmedLevel = level.trim();
          const foundLevel = allGSELevels.find((g) => g.code === trimmedLevel);
          return foundLevel || { id: 0, level: trimmedLevel, minGSE: 0, maxGSE: 0, code: '' }; // Handle unknown levels
        });

        const tutorRate = (() => {
          const rateCode = row['Regional tutor rate']?.split('-')[0].trim();
          return allLocations.find((loc) => loc.code === rateCode);
        })();

        return {
          startTime,
          weekday,
          category,
          GSELevel,
          tutorRate,
          vendor: row.Vendor.trim(),
        };
      } catch (error) {
        errors.push(`Row ${index + 1}: Failed to parse data due to unexpected format.`);
        return null;
      }
    })
    .filter(Boolean) as TemplateSession[];

  return { sessions, errors };
};

// export const convertToTemplateSession = (parsedData: any[]): TemplateSession[] => {
//   const findCategory = (category: string) =>
//     allCategories.find((cat) => cat.category === category.trim());

//   const findGSELevel = (code: string) => allGSELevels.find((level) => level.code === code.trim());

//   const findLocation = (rate: string) => {
//     const [code, location] = rate.split('-').map((s) => s.trim());
//     return allLocations.find(
//       (loc) => loc.code === code && loc.location.toLowerCase() === location.toLowerCase(),
//     );
//   };

//   return parsedData.map((item) => {
//     const categories = item.Categories.split(';')
//       .map((cat: string) => findCategory(cat))
//       .filter(Boolean);
//     const GSELevels = item['CEFR/GSE level']
//       .split(';')
//       .map((level: string) => findGSELevel(level))
//       .filter(Boolean);

//     const tutorRate = findLocation(item['Regional tutor rate']);

//     return {
//       startTime: item.Time.trim(),
//       weekday: item.Day === 'Weekday',
//       vendor: item.Vendor.trim(),
//       category: categories.map((cat: any) => ({
//         id: cat!.id,
//         category: cat!.category,
//       })),
//       GSELevel: GSELevels.map((level: any) => ({
//         id: level!.id,
//         level: level!.level,
//         minGSE: level!.minGSE,
//         maxGSE: level!.maxGSE,
//         code: level!.code,
//       })),
//       tutorRate: tutorRate
//         ? {
//             id: tutorRate.id,
//             location: tutorRate.location,
//             code: tutorRate.code,
//           }
//         : undefined,
//     };
//   });
// };

// Function to convert TemplateSession format back to the original parsed CSV format
export const convertToCSVFormat = (templateSessions: TemplateSession[]): ParsedSession[] => {
  return templateSessions.map((session) => {
    const categories = session.category.map((cat) => cat.category).join('; ');
    const GSELevels = session.GSELevel.map((level) => level.code).join('; ');
    const tutorRate = session.tutorRate
      ? `${session.tutorRate.code} - ${session.tutorRate.location}`
      : '';

    return {
      Time: session.startTime,
      Day: session.weekday ? 'Weekday' : 'Weekend',
      Categories: categories,
      'CEFR/GSE level': GSELevels,
      'Regional tutor rate': tutorRate,
      Vendor: session.vendor,
    };
  });
};

export const getOffset = (timeZone: string): number => {
  const match = timeZone.match(/^UTC([+-])(\d{2}):(\d{2})$/);
  if (match) {
    const sign = match[1] === '+' ? 1 : -1;
    const hours = parseInt(match[2], 10);
    const minutes = parseInt(match[3], 10);
    return sign * (hours + minutes / 60);
  } else {
    try {
      return dayjs.utc(new Date()).tz(timeZone).utcOffset() / 60;
    } catch (error) {
      console.log(error);
      return 0;
    }
  }
};

export const orderedLanguages: { code: string; language: string }[] = [
  { language: 'English', code: 'en_us' },
  { language: 'Spanish', code: 'es' },
  { language: 'Portuguese', code: 'br' },
  { language: 'Arabic', code: 'ar' },
  { language: 'Chinese', code: 'zh' },
  { language: 'French', code: 'fr' },
  { language: 'German', code: 'de' },
  { language: 'Italian', code: 'it' },
  { language: 'Polish', code: 'pl' },
  { language: 'Romanian', code: 'ro' },
  { language: 'Russian', code: 'ru' },
  { language: 'Vietnamese', code: 'vi' },
  { language: 'Japanese', code: 'ja' },
  { language: 'Korean', code: 'ko' },
  { language: 'Turkish', code: 'tr' },
  { language: 'Afrikaans', code: 'af' },
  { language: 'Bulgarian', code: 'bg' },
  { language: 'Czech', code: 'cz' },
  { language: 'Danish', code: 'da' },
  { language: 'Greek', code: 'el' },
  { language: 'Persian', code: 'fa' },
  { language: 'Finnish', code: 'fi' },
  { language: 'Hebrew', code: 'he' },
  { language: 'Hindi', code: 'hi' },
  { language: 'Croatian', code: 'hr' },
  { language: 'Hungarian', code: 'hu' },
  { language: 'Indonesian', code: 'id' },
  { language: 'Norwegian', code: 'no' },
  { language: 'Dutch', code: 'nl' },
  { language: 'Swedish', code: 'sv' },
  { language: 'Thai', code: 'th' },
  { language: 'Ukrainian', code: 'ukr' },
  { language: 'Bengali', code: 'bn' },
];

export class CustomApiError extends Error {
  status: number;
  response: ApiErrorResponse;

  constructor(message: string, status: number, response: ApiErrorResponse) {
    super(message);
    this.status = status;
    this.response = response;
  }
}
export enum AuthType {
  IES = 'ies',
  AZURE = 'azure',
}

export const isIesAuth = (): boolean => {
  const authType = sessionStorage.getItem('authType') as AuthType;
  return authType === AuthType.IES;
};
export const isAzureAuth = (): boolean => {
  const authType = sessionStorage.getItem('authType') as AuthType;
  return authType === AuthType.AZURE;
};

export const getRedirectUrl = () => {
  return `http://wizportal-lcl.pearson.com:3001/api/auth/callback/ies`;
};

export const getIesUrl = () => {
  const iesUrl = process.env.REACT_APP_IES_URL || '';
  if (!iesUrl) {
    throw new Error('IES url is missing');
  }
  return iesUrl;
};

export const getIamUrl = () => {
  const iamUrl = process.env.REACT_APP_IAM_URL || '';
  if (!iamUrl) {
    throw new Error('IAM url is missing');
  }
  return iamUrl;
};

export const getIesClientId = () => {
  const clientId = process.env.REACT_APP_IES_CLIENT_ID || '';
  if (!clientId) {
    throw new Error('Client ID is missing');
  }
  return clientId;
};

export const buildAuthHeaders = (
  token: string | undefined,
  isIes: boolean,
): Record<string, string> => {
  return isIes ? { 'x-Authorization': `Bearer ${token}` } : { Authorization: `Bearer ${token}` };
};

export const getIesLoginRedirectUrl = (state: string) => {
  const authorizeUrl =
    `${getIesUrl()}/auth/oauth2/pearson/authorize` +
    `?client_id=${encodeURIComponent(getIesClientId())}` +
    `&scope=openid` +
    `&response_type=code` +
    `&state=${encodeURIComponent(state)}` +
    `&redirect_uri=${encodeURIComponent(getRedirectUrl())}`;

  return authorizeUrl;
};
